import type { Check, Workflow } from './types'

import { baseAssistantOverviewUrl } from '@app/application/sf-assistant-checks/constants'
import {
  CheckStatus,
  checkStatus,
  CheckContext,
} from '@app/shared/types/sf-data'

function getSfAssistantElement(): HTMLElement | null {
  return document.getElementById('sfAssistantChecksHelper')
}

export function showSfAssistantChecks(): boolean {
  return !!getSfAssistantElement()
}

interface sfAssistantChecksData {
  showElement: boolean
  parsedAccountId?: number | undefined
  parsedReconciliationId?: number | undefined
  lastMessagebusId?: number | undefined
  channel?: string
  preemptive?: boolean
}

export function getSfAssistantChecksData(): sfAssistantChecksData {
  const sfAssistantElement = getSfAssistantElement()
  const showElement = !!sfAssistantElement
  let sfAssistantChecksData: sfAssistantChecksData = { showElement }

  if (showElement) {
    const data = sfAssistantElement.dataset

    const parsedAccountId = data.accountId
      ? parseInt(data.accountId)
      : undefined
    const parsedReconciliationId = data.reconciliationId
      ? parseInt(data.reconciliationId)
      : undefined
    const lastMessagebusId = data.lastMessagebusId
      ? parseInt(data.lastMessagebusId)
      : undefined
    const channel = data.messageBusChannel
    const preemptive = !!data.preemptive

    sfAssistantChecksData = {
      showElement,
      parsedAccountId,
      parsedReconciliationId,
      lastMessagebusId,
      channel,
      preemptive,
    }
  }

  return sfAssistantChecksData
}

export function checkIsDismissedOrCompleted(check: Check): boolean {
  return [
    checkStatus.DISMISSED as CheckStatus,
    checkStatus.COMPLETED as CheckStatus,
  ].includes(check.status)
}

export function checkIsCompleted(check: Check): boolean {
  return check.status === checkStatus.COMPLETED
}

export function doneChecks(
  checks: Check[],
  remarks: Record<string, Record<string, unknown>>
): Check[] {
  if (!checks) {
    return []
  }

  return checks.filter((check) => remarks?.[check.id]?.done === true)
}

export function pendingChecks(
  checks: Check[],
  remarks: Record<string, Record<string, unknown>>
): Check[] {
  if (!checks) {
    return []
  }

  return checks.filter((check) => remarks?.[check.id]?.done === false)
}

export function sfAssistantOverviewUrl(
  objectType: string,
  objectId: number | undefined
): string {
  const params = new URLSearchParams()
  params.append('locale', window.I18n.locale)
  params.append('country', window.Silverfin.currentFirmCountry)
  params.append('firm_id', window.Silverfin.currentFirmId)
  params.append('company_id', window.Silverfin.currentCompanyId)
  params.append('period_id', window.Silverfin.currentLedgerId().toString())
  params.append('object_type', objectType)
  params.append('object_id', (objectId || '').toString())

  return `${baseAssistantOverviewUrl}${params.toString()}`
}

export function getAssistantChecksPath(
  options?: Record<string, unknown>
): string {
  const v2 = window.Silverfin.currentFirmFeatures.supports_v2_checks
  const route = v2
    ? 'checks_ledger_assistant_checks_v2_index_path'
    : 'checks_ledger_assistant_checks_path'

  return window.Silverfin.Routes[route](
    window.Silverfin.currentFirmId,
    window.Silverfin.currentCompanyId,
    window.Silverfin.currentLedgerId(),
    options
  )
}

export function groupContextsById(
  contexts: CheckContext[]
): Record<string, CheckContext> {
  return contexts.reduce((acc, context) => {
    // We include context-type as part of the key/id to ensure
    // uniqueness of the context across different types
    const contextId = getComplexContextIdByTypeAndId(context.type, context.id)

    return {
      ...acc,
      [contextId]: context,
    }
  }, {})
}

export function getComplexContextIdByTypeAndId(
  type: string,
  contextId: number
): string {
  if (type === 'Account') {
    return `account__${contextId}`
  } else if (type === 'ReconciliationText') {
    return `reconciliation__${contextId}`
  }

  return `${contextId}`
}

export function getFilteredContextIds(
  contextById: Record<string, CheckContext>,
  workflow: Workflow
): string[] {
  return Object.keys(contextById).filter((contextId) => {
    const effectiveContextId = parseInt(contextId.split('__')[1], 10)
    const contextType = contextById[contextId].type

    if (contextType === 'Account') {
      return (workflow.accountIds || []).includes(effectiveContextId)
    } else if (contextType === 'ReconciliationText') {
      return (workflow.reconciliationTextIds || []).includes(effectiveContextId)
    }
    return false
  })
}

export function getChecksIdsByGroupId(
  checksByGroupId: Record<number, Check[]>
): Record<number, string[]> {
  return Object.keys(checksByGroupId).reduce((acc, groupId) => {
    const effectiveGroupId = parseInt(groupId, 10)
    return {
      ...acc,
      [effectiveGroupId]: checksByGroupId[effectiveGroupId].map(
        (check) => check.id
      ),
    }
  }, {})
}

export function getChecksWithStatusById(
  checksByGroupId: Record<number, Check[]>,
  readCheckIds: string[]
): Record<string, Check[]> {
  return Object.keys(checksByGroupId)
    .reduce((res, v) => {
      const effectiveGroupId = parseInt(v, 10)
      return res.concat(checksByGroupId[effectiveGroupId])
    }, [] as Check[])
    .reduce(
      (acc, check) => ({
        ...acc,
        [check.id]: {
          ...check,
          read: readCheckIds.includes(check.id),
          contextUrl: '',
          contextUrlText: '',
        },
      }),
      {}
    )
}

export const UNREAD_CHECKS = 'unread'
export const ALL_CHECKS = 'all'
export const GENERAL_CHECKS = 'general'
